import { useEffect, useState } from "react";
import { ScrollRestoration, useNavigate } from "react-router-dom";
import axios from "axios";
import Hero from "../../../components/Hero";
import Contact from "../../../components/Contact";

const services = [
  {
    title: "Cloud Accounting Setup",
    description:
      "Seamless migration and setup on Xero, MYOB, or QuickBooks with customised workflows for efficiency.",
  },
  {
    title: "End-to-End Bookkeeping",
    description:
      "Stay financially organised with accurate daily or weekly bookkeeping tailored for Australian practices. From bank reconciliations to up-to-date ledgers, we keep your books clean and reliable.",
  },
  {
    title: "AP/AR Management",
    description:
      "End-to-end accounts payable and receivable support including vendor onboarding, procure-to-pay workflows, collections follow-ups, order-to-cash reporting, and detailed ageing analysis.",
    description2:
      "Task-oriented approach to establish structured processes and streamline workflows ensuring control over financial operations."
  },
  {
    title: "Payroll Management",
    description:
      "STP-compliant payroll services including employee setup, PAYG calculations, TFN declarations, IAS workings, and payroll reporting.",
  },
  {
    title: "GST & BAS/IAS Compliance Support",
    description:
      "GST workings with BAS and IAS preparation, review, and compliance-ready submissions.",
  },
  {
    title: "Year-End Financial Statements",
    description:
      "Comprehensive financial reports prepared for audit readiness and corporate tax return finalisation.",
  },
  {
    title: "Management Reporting & MIS",
    description:
      "Insightful MIS reports, budgeting, forecasting, and financial analysis to support smarter business decisions.",
  },
  {
    title: "Expense & Travel Claims Support",
    description:
      "Monitoring and optimisation of travel and business expenses to improve cost control and compliance.",
  },
];

const faqs = [
  {
    question: "What services do you offer to Australian firms and businesses?",
    answer:
      "We support Australian accounting firms and businesses with bookkeeping, payroll, AP/AR management, BAS and GST compliance, financial statements, MIS reporting, and cloud accounting setup.",
  },
  {
    question: "Which accounting software do you work with?",
    answer:
      "We work with leading cloud platforms such as Xero, MYOB, and QuickBooks, and can adapt to your firm's preferred tools and workflows.",
  },
  {
    question: "Will our client data be secure?",
    answer:
      "Absolutely. We follow strict confidentiality policies, secure systems, and NDA-backed processes.",
  },
  {
    question: "Can you work as our extended team?",
    answer:
      "Yes. Many clients use us as their offshore support team. We integrate into your workflows, operate in the background, and can also liaise with your CPA firm as an extended back-office team.",
  },
  {
    question: "How do you price your services?",
    answer:
      "We offer flexible pricing models including monthly retainers, per-task pricing, or custom packages based on your needs and transaction volumes.",
  },
  {
    question: "How do we get started?",
    answer:
      "Reach out to us and we will discuss your requirements, share a customised scope, and start with a pilot if needed.",
  },
];

const edgePoints = [
  "Experienced Accounting Professionals",
  "Agile & Efficient Team",
  "Reduce Costs, Not Quality",
  "Save Time & Boost Productivity",
  "Scalable Support That Grows With You",
  "Secure & Reliable Processes",
  "Seamless Integration With You and Your Team",
  "A True Strategic Partner",
];

const Australia = () => {
  const [hero, setHero] = useState("");
  const [heroMob, setHeroMob] = useState("");
  const [openFaq, setOpenFaq] = useState(0);
  const width = window.innerWidth;
  const navigate = useNavigate();

  useEffect(() => {
    axios({
      url: "https://api.shopiq.app/api/contents/?identifier=bionadvisors.com",
      method: "get",
    })
      .then((res: any) => {
        if (res.data.contents[0].hero[2]) {
          setHero(res.data.contents[0].hero[2].mediaUrl);
          setHeroMob(res.data.contents[0].hero[2].mobMediaUrl);
        }
      })
      .catch((err: any) => {
        console.log(err);
      });
  }, []);

  return (
    <>
      <ScrollRestoration />
      <Hero
        text="YOUR TRUSTED OFFSHORE PARTNER FOR ACCURACY, COMPLIANCE & GROWTH"
        fontSize="46px"
        videoSrc={window.innerWidth < 800 ? hero : heroMob}
        styless={{
          fontSize: "40px",
          color: "#FFF",
          width: `${width < 1280 ? "100%" : "65%"}`,
          textAlign: `${width < 800 ? "left" : "right"}`,
          lineHeight: "1.2",
        }}
        isGlobalPage={true}
      />

      <section className="flex flex-col items-center justify-center w-full h-fit py-16 bg-[#fefefa] gap-12">
        {/* Two-column: heading/description left, image right */}
        <section className="bg-[#BB9C71] h-fit p-10 md:pt-20 md:pl-20 md:pr-0 md:pb-0 flex">
          <div className="w-full h-full flex flex-col md:flex-row items-center justify-center md:justify-end gap-6 md:gap-1">
            <p
              className="w-full lg:w-[33%] h-full text-[#F9F1D8] flex flex-col items-start justify-center lg:gap-2
            text-left "
              style={{
                fontFamily: "Josefin Sans, sans-serif",
              }}
            >
              <h1 className="text-4xl uppercase w-full text-[#fefefa]">
                AUSTRALIA DESK
              </h1>
              <p className="text-justify text-base w-[90%]">
                We help Australian accounting firms and businesses streamline
                operations, reduce costs, and stay fully compliant, while you focus
                on growth.
              </p>
            </p>
            <img
              src="https://business-one-1.s3.ap-south-1.amazonaws.com/fk64kddipo3e%2F1774351351380_urlaubstracker-dW8dOC8r7O4-unsplash.webp"
              alt="card"
              className="w-full md:w-3/5 h-[200px] md:h-[30vw] object-cover"
            />
          </div>
        </section>

        <div className="w-[85%] md:w-[75%] grid grid-cols-1 md:grid-cols-2 gap-5">
          {services.map((item, index) => (
            <div
              key={item.title}
              className="border border-[#d8c8a8] bg-[#fffdf9] p-6 md:p-7 shadow-[0_6px_18px_rgba(187,156,113,0.12)]"
              style={{ fontFamily: "Josefin Sans, sans-serif" }}
            >
              <div className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-full bg-[#BB9C71] text-[#fefefa] flex items-center justify-center text-lg">
                  {index + 1}
                </span>
                <h2 className="text-[1.4rem] leading-tight text-[#BB9C71]">
                  {item.title}
                </h2>
              </div>
              <p className="text-[#464646] text-base mt-4 leading-7">
                {item.description}
              </p>
              {item?.description2 && <p className="text-[#464646] text-base mt-2 leading-7">
                {item.description2}
              </p>}
            </div>
          ))}
        </div>
      </section>

      <section className="w-full bg-[#fefefa] py-16 flex flex-col items-center gap-10">
        <div
          className="w-[85%] md:w-[75%]"
          style={{ fontFamily: "Josefin Sans, sans-serif" }}
        >
          <div className="flex flex-col">
            {faqs.map((faq, index) => (
              <div
                key={faq.question}
                className={`bg-[#fefefa] border-b border-[#e5e7eb] transition-all duration-300 relative ${
                  openFaq === index ? "border-l-4 border-l-[#BB9C71]" : ""
                }`}
                style={{
                  borderLeftColor: openFaq === index ? "#BB9C71" : "transparent",
                  borderLeftWidth: openFaq === index ? "3px" : "0px",
                }}
              >
                <button
                  className="w-full text-left px-5 py-5 flex items-center justify-between text-[#314256] gap-4 bg-transparent outline-none"
                  onClick={() => {
                    if (openFaq !== index) {
                      setOpenFaq(index);
                    }
                  }}
                >
                  <span className="text-lg leading-6 font-semibold md:text-xl">{faq.question}</span>
                  <span className="min-w-[24px] flex items-center justify-center">
                    {openFaq === index ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="11" stroke="#BB9C71" strokeWidth="1"/>
                        <path d="M12 16L12 8M12 8L8 12M12 8L16 12" stroke="#BB9C71" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="11" stroke="#9ca3af" strokeWidth="1"/>
                        <path d="M12 8L12 16M12 16L16 12M12 16L8 12" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </span>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    openFaq === index ? "max-h-96 opacity-100 pb-5" : "max-h-0 opacity-0 pb-0"
                  }`}
                >
                  <p className="px-5 text-[#6b7280] text-base leading-7">
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-[#fefefa] py-16 flex flex-col items-center gap-10">
        <div
          className="w-[85%] md:w-[75%] flex flex-col gap-6 text-[#314256] bg-[#fffdf9] border border-[#d8c8a8] p-7 md:p-10"
          style={{ fontFamily: "Josefin Sans, sans-serif" }}
        >
          <h2 className="text-4xl text-[#BB9C71] text-center">
            Strategic Partner, Not Just Offshore Support
          </h2>
          <p className="text-base text-justify leading-7">
            For businesses, we act as a liaison with your CPA firm externally
            and coordinate closely with your internal teams and clients,
            ensuring smooth communication, consistent reporting, and a seamless
            experience end to end.
          </p>
          <p className="text-base text-justify leading-7">
            For CPA firms, our offshore delivery model enables you to
            significantly reduce operating costs and time spent on routine and
            execution-heavy tasks while maintaining high standards of quality,
            governance, and compliance. This allows your leadership team to
            stay focused on strategic advisory, client relationships, and
            scalable growth, without operational friction.
          </p>
        </div>

        <div
          className="w-[85%] md:w-[75%] border border-[#d8c8a8] p-8 bg-[#fffdf9] shadow-[0_8px_22px_rgba(187,156,113,0.12)]"
          style={{ fontFamily: "Josefin Sans, sans-serif" }}
        >
          <h2 className="text-4xl text-[#BB9C71] text-center mb-6">
            The Bion Edge
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {edgePoints.map((point) => (
              <p key={point} className="text-[#314256] text-lg flex items-start gap-2">
                <span className="text-[#BB9C71]">✓</span>
                <span>{point}</span>
              </p>
            ))}
          </div>
        </div>

        <button
          className="text-[#BB9C71] bg-[#fefefa] border-2 border-solid border-[#BB9C71] py-2 px-4 rounded-sm text-base w-fit h-fit hover:bg-[#BB9C71] hover:text-[#fefefa] cursor-pointer"
          onClick={() => {
            navigate("/services/global/dubai");
          }}
        >
          View Global Services (Dubai)
        </button>
      </section>

      <Contact />
    </>
  );
};

export default Australia;